import type { UIMessage } from "ai";
import type {
	ChatbotConversation,
	ChatbotConversationDetails,
	ChatbotInstructions,
	ChatbotLimits,
	ChatbotMessageMetadata,
} from "src/components/GlobalChatbot/types";
import isPes from "src/helpers/isPes";
import newApi, { http } from "src/newApi";
import { ItemTypes, PromptType } from "src/types/enums";

const prefix = () => (isPes() ? "/pes" : "");

interface ConversationResponse {
	id: number;
	title: string;
	createdAt: string;
}

function toConversation(c: ConversationResponse): ChatbotConversation {
	return { id: String(c.id), question: c.title, createdAt: c.createdAt };
}

export function getConversations(): Promise<ChatbotConversation[]> {
	return http
		.get(`/v1${prefix()}/ai/content/conversations`)
		.then(({ data }) => data.data.map(toConversation));
}

interface PromptContext {
	id: number;
	question: string;
	answers: string[];
	correctAnswer: string;
}

interface ConversationTurn {
	id: number;
	// null = tura w trakcie generowania (MessageBubble pokazuje wtedy loader)
	response: string | null;
	userInput: {
		userPrompt: string;
		questionId?: number;
		// Stare tury mają tu string (gotowy blok tekstu) — wtedy nie pokazujemy kontekstu.
		promptContext?: PromptContext | string;
	};
	createdAt: string;
}

interface ConversationResponses {
	responses: ConversationTurn[];
	conversationDetails?: { questions: PromptContext[] };
}

const stripHtml = (html: string) => html.replace(/<[^>]*>/g, "");

function toMetadata(
	promptContext: ConversationTurn["userInput"]["promptContext"],
): ChatbotMessageMetadata | undefined {
	if (!promptContext || typeof promptContext === "string") return undefined;
	return { questionPreview: stripHtml(promptContext.question) };
}

// Backend zwraca same pytania — liczbę zapytań i godzinę wyliczamy z tur.
function toDetails({
	responses,
	conversationDetails,
}: ConversationResponses): ChatbotConversationDetails {
	return {
		promptsCount: responses.length,
		questions: (conversationDetails?.questions ?? []).map(
			({ id, question }) => {
				const turns = responses.filter(
					({ userInput }) => userInput.questionId === id,
				);
				return {
					id,
					preview: stripHtml(question),
					promptsCount: turns.length,
					askedAt: turns[0]?.createdAt,
				};
			},
		),
	};
}

export function getConversationMessages(
	conversationId: string,
): Promise<{ messages: UIMessage[]; details: ChatbotConversationDetails }> {
	return newApi
		.getAiChatResponses({
			promptType: PromptType.CONVERSATION,
			itemType: ItemTypes.CONVERSATION,
			itemId: conversationId,
		})
		.then((data: ConversationResponses) => ({
			details: toDetails(data),
			messages: data.responses.flatMap(
				({ id, response, userInput }): UIMessage[] => [
					{
						id: `u-${id}`,
						role: "user",
						metadata: toMetadata(userInput.promptContext),
						parts: [{ type: "text", text: userInput.userPrompt }],
					},
					{
						id: `b-${id}`,
						role: "assistant",
						parts: [{ type: "text", text: response ?? "" }],
					},
				],
			),
		}));
}

export function createConversation(
	question: string,
): Promise<ChatbotConversation> {
	return http
		.post(`/v1${prefix()}/ai/content/conversations`, {
			specId: newApi.getUserSpecialization(),
			firstMessage: question,
		})
		.then(({ data }) => toConversation(data));
}

export function deleteConversation(conversationId: string): Promise<void> {
	return http
		.delete(`/v1${prefix()}/ai/content/conversations/${conversationId}`)
		.then(() => undefined);
}

export function getConversationLimits(): Promise<ChatbotLimits> {
	return http
		.get(`/v1${prefix()}/ai/content/conversations/limits`)
		.then(({ data }) => data.user);
}

export function getChatbotInstructions(): Promise<ChatbotInstructions> {
	return http
		.get(`/v1${prefix()}/ai/content/conversations/howToUse`)
		.then(({ data }) => data);
}
